"use client"

import React, { useMemo, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { defaultStyles, JsonView } from "react-json-view-lite"

import { BasketContext, BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"

import "react-json-view-lite/dist/index.css"

import { CircleMinus, CirclePlus } from "lucide-react"

import { Button, Label, Switch } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import { UbBasketDebugStoryblok } from "@/components/storyblok/component-types"

interface BasketDebugProps {
  basketContext: BasketContext
  blok: UbBasketDebugStoryblok
}

export default function BasketDebugClient(props: BasketDebugProps) {
  const isB2B = props.basketContext.isB2B
  const isAnonymous = props.basketContext.isAnonymous

  const [expanded, setExpanded] = useState<boolean>(false)

  const { basketData, basketLineItems, basketPromotionCodes, basketPayments, setShowAllPrices, showAllPrices } =
    useUbStorefrontState()

  const basketTotalData: BasketTotalData = useMemo(() => {
    return determineBasketTotalData(
      isB2B,
      basketData,
      basketLineItems,
      basketPromotionCodes,
      isAnonymous,
      props.basketContext.isoLocale
    )
  }, [basketData, basketLineItems, basketPromotionCodes, isB2B, isAnonymous, props.basketContext.isoLocale])

  function toggleExpanded() {
    setExpanded(!expanded)
  }

  return (
    <>
      {basketData && basketLineItems && (
        <div className={"relative z-[200]"}>
          <div
            className={cn("fixed left-0 top-0 h-auto min-h-8 min-w-8 overflow-auto sm:w-[32rem]", {
              "sm:w-8": !expanded,
            })}
          >
            <Button
              size={"smNoGrow"}
              variant={"ghost"}
              onClick={toggleExpanded}
              className={"absolute left-1 top-1 size-6 p-1"}
            >
              <>{expanded ? <CircleMinus className={"size-4"} /> : <CirclePlus className={"size-4"} />}</>
            </Button>
            <div
              className={cn("block pt-6 px-2 bg-gray-100", {
                hidden: !expanded,
              })}
            >
              <p className={"bg-gray-100 pl-3 text-xs"}>Raw Basket Data</p>
              <JsonView data={basketData} shouldExpandNode={() => false} style={defaultStyles} />
              <p className={"bg-gray-100pl-3 text-xs"}>Raw Basket Payments</p>
              <JsonView data={basketPayments} shouldExpandNode={() => false} style={defaultStyles} />
              <p className={"bg-gray-100 pl-3 text-xs"}>Extracted Basket Total Data</p>
              <JsonView data={basketTotalData} shouldExpandNode={() => false} style={defaultStyles} />
              <p className={"bg-gray-100 pl-3 text-xs"}>Extracted Basket Line Items</p>
              <JsonView data={basketLineItems} shouldExpandNode={() => false} style={defaultStyles} />

              <div className={"p-2 flex flex-row gap-2 items-center justify-between"}>
                <Label htmlFor="show-all-prices-switch" className={cn("tracking-tight")}>
                  {"Show all prices"}
                </Label>
                <Switch
                  aria-labelledby="show-all-prices-switch"
                  id="show-all-prices-switch"
                  className={"data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"}
                  checked={showAllPrices}
                  onCheckedChange={() => setShowAllPrices(!showAllPrices)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
